import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject, model } from "@angular/core"
import { AgColor, AgType } from "@shared/components/flow-badge-v2/flow-badge-v2.component"
import { IconImageService } from "@shared/services/icon-image.service"

let uniqueId = 0

export type CardTitleSize = "large" | "small"
export type CardLeadingIconVariant = "plain" | "boxed"

export interface FlowCardAction { id: string; label?: string; type?: "primary" | "secondary" | "destructive"; icon?: string; disabled?: boolean; loading?: boolean; ariaLabel?: string }
export interface FlowCardBadge { text: string; color?: AgColor; type?: AgType; icon?: string }

@Component({
    selector: "flow-card-v2",
    templateUrl: "./flow-card-v2.component.html",
    styleUrl: "./flow-card-v2.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FlowCardV2Component {
  @Input({ required: true }) title!: string
  @Input() titleSize: CardTitleSize = "large"
  @Input() headingLevel: 2 | 3 = 3
  @Input() subtext = ""
  @Input() leadingIcon = ""
  @Input() leadingIconVariant: CardLeadingIconVariant = "plain"
  @Input() titleIcon = ""
  @Input() showCheckbox = false
  @Input() checkboxType: "checkbox" | "radio" = "checkbox"
  @Input() checkboxDisabled = false
  @Input() checkboxAriaLabel = "Select"
  @Input() badge?: FlowCardBadge
  @Input() badgeText = ""
  @Input() badgeColor: AgColor = "Neutral"
  @Input() badgeType: AgType = "Subtle"
  @Input() actions: FlowCardAction[] = []
  @Input() destructiveLabel = ""
  @Input() secondaryLabel = ""
  @Input() primaryLabel = ""
  @Input() showOverflow = false
  @Input() overflowAriaLabel = "More actions"
  @Input() trailingBadge?: FlowCardBadge
  @Input() trailingBadgeText = ""
  @Input() trailingBadgeColor: AgColor = "Neutral"
  @Input() trailingBadgeType: AgType = "Subtle"
  @Input() disabledText = ""
  @Input() collapsible = false
  @Input() bodyPadding = "20px"
  @Input() chevronAriaLabel = "Toggle details"
  @Input() set trailingIconAriaLabel(value: string) {
    this.chevronAriaLabel = value
  }

  @Output() actionClick = new EventEmitter<string>()
  @Output() destructiveClick = new EventEmitter<MouseEvent>()
  @Output() secondaryClick = new EventEmitter<MouseEvent>()
  @Output() primaryClick = new EventEmitter<MouseEvent>()
  @Output() overflowClick = new EventEmitter<MouseEvent>()

  public expanded = model(true)
  public checkboxChecked = model(false)

  public readonly iconService = inject(IconImageService)
  private readonly instanceId = uniqueId++
  public readonly titleId = `flow-card-title-${this.instanceId}`
  public readonly subtextId = `flow-card-subtext-${this.instanceId}`
  public readonly contentId = `flow-card-content-${this.instanceId}`

  public get titleTextStyle(): "body-2-medium" | "body-3-medium" {
    return this.titleSize === "small" ? "body-3-medium" : "body-2-medium"
  }

  public get resolvedActions(): FlowCardAction[] {
    if (this.actions.length) return this.actions

    const actions: FlowCardAction[] = []
    if (this.destructiveLabel) actions.push({ id: "destructive", label: this.destructiveLabel, type: "destructive" })
    if (this.secondaryLabel) actions.push({ id: "secondary", label: this.secondaryLabel, type: "secondary" })
    if (this.primaryLabel) actions.push({ id: "primary", label: this.primaryLabel, type: "primary" })
    return actions
  }

  public get inlineBadge(): FlowCardBadge | null {
    return this.resolveBadge(this.badge, this.badgeText, this.badgeColor, this.badgeType)
  }

  public get resolvedTrailingBadge(): FlowCardBadge | null {
    return this.resolveBadge(this.trailingBadge, this.trailingBadgeText, this.trailingBadgeColor, this.trailingBadgeType)
  }

  public get hasActions(): boolean {
    return !!this.resolvedActions.length || this.showOverflow
  }

  public onActionClick(action: FlowCardAction, event: MouseEvent): void {
    this.actionClick.emit(action.id)
    if (this.actions.length) return

    if (action.id === "destructive") this.destructiveClick.emit(event)
    if (action.id === "secondary") this.secondaryClick.emit(event)
    if (action.id === "primary") this.primaryClick.emit(event)
  }

  public onCheckboxToggle(): void {
    if (this.checkboxDisabled) return
    this.checkboxChecked.update((checked) => !checked)
  }

  public toggleExpanded(): void {
    this.expanded.update((expanded) => !expanded)
  }

  // the whole header is the hit target, not just the chevron. Controls inside it stop the
  // event themselves, so anything reaching here is a click on the card's own surface
  public onHeaderClick(): void {
    if (!this.collapsible) return
    this.toggleExpanded()
  }

  public onHeaderKeydown(event: Event): void {
    if (!this.collapsible) return
    event.preventDefault()
    this.toggleExpanded()
  }

  private resolveBadge(config: FlowCardBadge | undefined, text: string, color: AgColor, type: AgType): FlowCardBadge | null {
    if (config) return config
    return text ? { text, color, type } : null
  }
}
